const {createQueue, reconcile, entitlement, managed} = await import(
  document.getElementById('padellic-gift-state').dataset.coreUrl
);
import {CartLinesUpdateEvent, StandardEvents} from '@shopify/events';

const initial = JSON.parse(document.getElementById('padellic-gift-state').textContent);
const nativeFetch = window.fetch.bind(window);
const localQueue = createQueue();
const enqueue = task => localQueue(() => navigator.locks ?
  navigator.locks.request('padellic-cart-gift-2027', task) : task());
const root = window.Shopify?.routes?.root || '/';
let scheduled;

function message(text = '') {
  const element = document.getElementById('padellic-gift-message');
  element.textContent = text;
  element.hidden = !text;
}

async function request(path, body) {
  const response = await nativeFetch(`${root}cart/${path}`, {
    method: 'POST', credentials: 'same-origin',
    headers: {'Content-Type': 'application/json', Accept: 'application/json'},
    body: JSON.stringify(body), signal: AbortSignal.timeout(15000)
  });
  if (!response.ok) throw new Error('Het cadeau kon niet worden bijgewerkt. Controleer de voorraad en probeer opnieuw.');
  return response.json();
}

async function read() {
  const response = await nativeFetch(`${root}cart?view=padellic-gift&_=${Date.now()}`, {
    credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000)
  });
  if (!response.ok) throw new Error('De winkelwagen kon niet worden gecontroleerd. Probeer opnieuw.');
  const state = JSON.parse(await response.text());
  if (!Array.isArray(state.items) || !state.config ||
      (state.config.enabled && !state.config.variantId)) throw new Error('De cadeau-instellingen zijn onvolledig.');
  if (entitlement(state) && !state.config.available) throw new Error('Het kerstcadeau is tijdelijk uitverkocht. Neem contact met ons op.');
  return state;
}

async function refresh() {
  const response = await nativeFetch(`${root}cart.js`, {credentials: 'same-origin', cache: 'no-store'});
  if (!response.ok) throw new Error('De winkelwagen kon niet worden ververst.');
  const cart = await response.json();
  // Vessel 4.1.5 consumes this promise and reloads its own cart sections.
  document.dispatchEvent(new CartLinesUpdateEvent({action: 'update', context: 'cart',
    lines: [], promise: Promise.resolve({cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {source: 'padellic-gift', itemCount: cart.item_count}})}));
}

const sync = () => enqueue(() => reconcile({read, write: request, changed: refresh}));
function schedule() {
  clearTimeout(scheduled);
  scheduled = setTimeout(() => sync().then(() => message()).catch(error => message(error.message)), 80);
}

if (initial.config.enabled || initial.items.some(line => managed(line, initial.config))) {
  // Serialize fetch-based cart writes with gift writes within this tab.
  // Existing Response objects are returned untouched to preserve theme contracts.
  window.fetch = (input, options) => {
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.href);
    const method = (options?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const cartWrite = url.origin === location.origin && method === 'POST' &&
      /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(url.pathname);
    if (!cartWrite) return nativeFetch(input, options);
    return enqueue(async () => {
      const response = await nativeFetch(input, options);
      schedule();
      return response;
    });
  };

  // Standard events are emitted before their cart requests finish; await promise.
  for (const name of [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate]) {
    document.addEventListener(name, event => {
      event.promise?.then(({detail}) => {
        if (detail?.source !== 'padellic-gift') schedule();
      }).catch(() => {});
    });
  }
  window.addEventListener('pageshow', schedule);
  window.addEventListener('focus', schedule);
  document.addEventListener('visibilitychange', () => {if (!document.hidden) schedule();});

  let proceeding = false;
  async function checkout() {
    if (proceeding) return;
    proceeding = true;
    message('Je kerstcadeau wordt gecontroleerd…');
    try {
      clearTimeout(scheduled);
      await sync();
      location.assign(`${root}checkout`);
    } catch (error) {
      message(error.message);
      document.getElementById('padellic-gift-message').scrollIntoView({block: 'center'});
      proceeding = false;
    }
  }
  document.addEventListener('click', event => {
    const control = event.target instanceof Element ? event.target.closest('a[href],button[name="checkout"],input[name="checkout"]') : null;
    if (!control) return;
    const url = control instanceof HTMLAnchorElement ? new URL(control.href) : null;
    if (url && !(url.origin === location.origin && /\/checkout\/?$/.test(url.pathname))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void checkout();
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (event.submitter?.name !== 'checkout' && !/\/checkout\/?$/.test(new URL(form.action).pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void checkout();
  }, true);
  window.PadellicGift = {sync};
  schedule();
}
